import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Select2Field from "components/Select2Field";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import axios from "axios";
import debounce from "debounce";
import Swal from "sweetalert2";
import api from "services/api";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { totalPagesFromCount } from "utils/paginationStats";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?provid=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?dptid=${dptid}`,
  establishments: (sptid) => `${apiurl}Assignment/establishments?sptid=${sptid}`,
};

const NOTIF_API = {
  send: "/Notification/send",
  history: "/Notification/history",
};

const TEACHER_SCOPES = ["all", "filtered"];

function normalizeCsv(val) {
  if (Array.isArray(val)) return val.filter(Boolean).map(String).join(",");
  if (typeof val === "string" && val.trim()) {
    return val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(",");
  }
  return "";
}

function buildDeviceTypesCsv({ android, ios }) {
  return [android ? "1" : null, ios ? "2" : null].filter(Boolean).join(",");
}

function jsonHeaders() {
  return {
    ...authHeaders(),
    "Content-Type": "application/json-patch+json",
  };
}

function parseHistoryList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function extractHistoryTotal(data, list) {
  return (
    data?.stats?.totalCount ??
    data?.totalCount ??
    data?.totalRecords ??
    data?.total ??
    data?.totalResults ??
    list[0]?.totalCount ??
    list.length
  );
}

function formatDateTime(value) {
  if (!value) return "—";
  const raw = String(value);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.replace("T", " ").replace(/\.\d+$/, "");
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateText(value, max = 72) {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function parseDeviceTypes(deviceTypesCsv) {
  const csv = normalizeCsv(deviceTypesCsv);
  if (!csv) return [];
  return csv.split(",").map((s) => s.trim()).filter(Boolean);
}

function statusTone(status) {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("complete") || s.includes("success")) {
    return { bg: "rgba(34, 197, 94, 0.12)", color: "#15803d", border: "rgba(34, 197, 94, 0.35)" };
  }
  if (s.includes("fail") || s.includes("error")) {
    return { bg: "rgba(239, 68, 68, 0.12)", color: "#b91c1c", border: "rgba(239, 68, 68, 0.35)" };
  }
  if (s.includes("pending") || s.includes("queue") || s.includes("process")) {
    return { bg: "rgba(245, 158, 11, 0.12)", color: "#b45309", border: "rgba(245, 158, 11, 0.35)" };
  }
  return { bg: "rgba(100, 116, 139, 0.12)", color: figmaTokens.textSecondary, border: figmaTokens.borderSubtle };
}

function jobTypeTone(jobType) {
  const t = String(jobType ?? "").toLowerCase();
  if (t === "teacher") return { bg: "rgba(59, 130, 246, 0.1)", color: "#1d4ed8" };
  if (t === "admin") return { bg: "rgba(139, 92, 246, 0.1)", color: "#6d28d9" };
  if (t === "recruiter") return { bg: "rgba(16, 185, 129, 0.1)", color: "#047857" };
  return { bg: "rgba(100, 116, 139, 0.1)", color: figmaTokens.textSecondary };
}

const makeOptions = (data, ids, labels) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));

const fieldLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

const swalApp = {
  confirmButtonColor: figmaTokens.accent,
  cancelButtonColor: figmaTokens.textMuted,
  background: figmaTokens.surface,
  color: figmaTokens.textPrimary,
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function Notifications() {
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [teacherScope, setTeacherScope] = useState("all");
  const [provinceIds, setProvinceIds] = useState("");
  const [departmentIds, setDepartmentIds] = useState("");
  const [subPrefectureIds, setSubPrefectureIds] = useState("");
  const [establishmentIds, setEstablishmentIds] = useState("");
  const [sendAndroid, setSendAndroid] = useState(true);
  const [sendIos, setSendIos] = useState(true);

  const [userProvinceIds, setUserProvinceIds] = useState("");
  const [userDepartmentIds, setUserDepartmentIds] = useState("");
  const [userSubPrefectureIds, setUserSubPrefectureIds] = useState("");
  const [userEstablishmentIds, setUserEstablishmentIds] = useState("");

  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);
  const [estabOpts, setEstabOpts] = useState([]);
  const [userDeptOpts, setUserDeptOpts] = useState([]);
  const [userSubpOpts, setUserSubpOpts] = useState([]);
  const [userEstabOpts, setUserEstabOpts] = useState([]);

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  const cache = useRef({ provinces: null, departments: {}, subprefectures: {}, establishments: {} });
  const controllers = useRef({});

  const cancelRequest = (key) => {
    if (controllers.current[key]) controllers.current[key].abort();
    controllers.current[key] = new AbortController();
    return controllers.current[key].signal;
  };

  const scopeLabels = useMemo(
    () => ({
      all: t("notifications.scope.all", "All teachers"),
      filtered: t("notifications.scope.filtered", "By location"),
    }),
    [t]
  );

  const fetchProvinces = useCallback(async () => {
    if (cache.current.provinces) {
      setProvOpts(cache.current.provinces);
      return;
    }
    try {
      const { data } = await axios.get(GEO_API.provinces, { headers: authHeaders() });
      const opts = makeOptions(data, ["provid", "id"], ["name", "province"]);
      cache.current.provinces = opts;
      setProvOpts(opts);
    } catch (err) {
      console.error("Fetch provinces error", err);
    }
  }, []);

  const fetchDepartmentsForProvinces = useCallback(
    debounce(async (provIdsRaw) => {
      const provIds = normalizeCsv(provIdsRaw).split(",").filter(Boolean);
      if (!provIds.length) {
        setDeptOpts([]);
        return;
      }
      try {
        const merged = [];
        const seen = new Set();
        await Promise.all(
          provIds.map(async (provId) => {
            if (cache.current.departments[provId]) {
              cache.current.departments[provId].forEach((opt) => {
                const key = String(opt.id);
                if (!seen.has(key)) {
                  seen.add(key);
                  merged.push(opt);
                }
              });
              return;
            }
            const { data } = await axios.get(GEO_API.departments(provId), {
              signal: cancelRequest(`departments-multi-${provId}`),
              headers: authHeaders(),
            });
            const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
            cache.current.departments[provId] = opts;
            opts.forEach((opt) => {
              const key = String(opt.id);
              if (!seen.has(key)) {
                seen.add(key);
                merged.push(opt);
              }
            });
          })
        );
        merged.sort((a, b) => String(a.text).localeCompare(String(b.text)));
        setDeptOpts(merged);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch departments error", err);
      }
    }, 300),
    []
  );

  const fetchDepartmentsForUserProvinces = useCallback(
    debounce(async (provIdsRaw) => {
      const provIds = normalizeCsv(provIdsRaw).split(",").filter(Boolean);
      if (!provIds.length) {
        setUserDeptOpts([]);
        return;
      }
      try {
        const merged = [];
        const seen = new Set();
        await Promise.all(
          provIds.map(async (provId) => {
            if (cache.current.departments[provId]) {
              cache.current.departments[provId].forEach((opt) => {
                const key = String(opt.id);
                if (!seen.has(key)) {
                  seen.add(key);
                  merged.push(opt);
                }
              });
              return;
            }
            const { data } = await axios.get(GEO_API.departments(provId), {
              signal: cancelRequest(`departments-user-${provId}`),
              headers: authHeaders(),
            });
            const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
            cache.current.departments[provId] = opts;
            opts.forEach((opt) => {
              const key = String(opt.id);
              if (!seen.has(key)) {
                seen.add(key);
                merged.push(opt);
              }
            });
          })
        );
        merged.sort((a, b) => String(a.text).localeCompare(String(b.text)));
        setUserDeptOpts(merged);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch user departments error", err);
      }
    }, 300),
    []
  );

  const resolveDepartmentIds = useCallback(async (provIdsRaw, deptIdsRaw) => {
    const deptIds = normalizeCsv(deptIdsRaw).split(",").filter(Boolean);
    if (deptIds.length) return deptIds;

    const provIds = normalizeCsv(provIdsRaw).split(",").filter(Boolean);
    if (!provIds.length) return [];

    const merged = [];
    const seen = new Set();
    await Promise.all(
      provIds.map(async (provId) => {
        if (!cache.current.departments[provId]) {
          const { data } = await axios.get(GEO_API.departments(provId), { headers: authHeaders() });
          cache.current.departments[provId] = makeOptions(data, ["dptid", "id"], ["name", "department"]);
        }
        cache.current.departments[provId].forEach((opt) => {
          const key = String(opt.id);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(key);
          }
        });
      })
    );
    return merged;
  }, []);

  const mergeGeoOptions = useCallback(async (ids, cacheKey, fetchFn, idKeys, labelKeys) => {
    const merged = [];
    const seen = new Set();
    await Promise.all(
      ids.map(async (parentId) => {
        if (cache.current[cacheKey][parentId]) {
          cache.current[cacheKey][parentId].forEach((opt) => {
            const key = String(opt.id);
            if (!seen.has(key)) {
              seen.add(key);
              merged.push(opt);
            }
          });
          return;
        }
        const { data } = await fetchFn(parentId);
        const opts = makeOptions(data, idKeys, labelKeys);
        cache.current[cacheKey][parentId] = opts;
        opts.forEach((opt) => {
          const key = String(opt.id);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(opt);
          }
        });
      })
    );
    merged.sort((a, b) => String(a.text).localeCompare(String(b.text)));
    return merged;
  }, []);

  const fetchSubprefecturesForScope = useCallback(
    debounce(async ({ provIdsRaw, deptIdsRaw, requestKey, setOptions }) => {
      const deptIds = await resolveDepartmentIds(provIdsRaw, deptIdsRaw);
      if (!deptIds.length) {
        setOptions([]);
        return;
      }
      try {
        const merged = await mergeGeoOptions(
          deptIds,
          "subprefectures",
          async (deptId) =>
            axios.get(GEO_API.subprefectures(deptId), {
              signal: cancelRequest(`${requestKey}-subprefectures-${deptId}`),
              headers: authHeaders(),
            }),
          ["sptid", "id"],
          ["name", "subprefecture"]
        );
        setOptions(merged);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch subprefectures error", err);
      }
    }, 300),
    [mergeGeoOptions, resolveDepartmentIds]
  );

  const fetchEstablishmentsForScope = useCallback(
    debounce(async ({ provIdsRaw, deptIdsRaw, subpIdsRaw, requestKey, setOptions }) => {
      let subpIds = normalizeCsv(subpIdsRaw).split(",").filter(Boolean);
      if (!subpIds.length) {
        const deptIds = await resolveDepartmentIds(provIdsRaw, deptIdsRaw);
        if (!deptIds.length) {
          setOptions([]);
          return;
        }
        const subpOptsMerged = await mergeGeoOptions(
          deptIds,
          "subprefectures",
          async (deptId) =>
            axios.get(GEO_API.subprefectures(deptId), {
              signal: cancelRequest(`${requestKey}-establishments-subprefectures-${deptId}`),
              headers: authHeaders(),
            }),
          ["sptid", "id"],
          ["name", "subprefecture"]
        );
        subpIds = subpOptsMerged.map((opt) => String(opt.id)).filter(Boolean);
      }
      if (!subpIds.length) {
        setOptions([]);
        return;
      }
      try {
        const merged = await mergeGeoOptions(
          subpIds,
          "establishments",
          async (subpId) =>
            axios.get(GEO_API.establishments(subpId), {
              signal: cancelRequest(`${requestKey}-establishments-${subpId}`),
              headers: authHeaders(),
            }),
          ["id"],
          ["name", "establishment"]
        );
        setOptions(merged);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch establishments error", err);
      }
    }, 300),
    [mergeGeoOptions, resolveDepartmentIds]
  );

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(NOTIF_API.history, {
        params: { pageNumber: historyPage, pageSize: historyPageSize },
        headers: authHeaders(),
      });
      const list = parseHistoryList(data);
      setHistory(list);
      setHistoryTotal(extractHistoryTotal(data, list));
    } catch {
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyPageSize]);

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    setError("");
  }, [
    activeTab,
    teacherScope,
    provinceIds,
    departmentIds,
    subPrefectureIds,
    establishmentIds,
    sendAndroid,
    sendIos,
    userProvinceIds,
    userDepartmentIds,
    userSubPrefectureIds,
    userEstablishmentIds,
    title,
    body,
  ]);

  const deviceTypesCsv = useMemo(
    () => buildDeviceTypesCsv({ android: sendAndroid, ios: sendIos }),
    [sendAndroid, sendIos]
  );

  const sendPayload = useMemo(() => {
    const base = {
      title: title.trim(),
      message: body.trim(),
      deviceTypesCsv,
    };

    if (activeTab === 0) {
      const payload = {
        ...base,
        audienceType: "teacher",
        allTeachers: teacherScope === "all",
      };
      if (teacherScope === "filtered") {
        const provCsv = normalizeCsv(provinceIds);
        const deptCsv = normalizeCsv(departmentIds);
        const subpCsv = normalizeCsv(subPrefectureIds);
        const estabCsv = normalizeCsv(establishmentIds);
        if (provCsv) payload.provinceIdsCsv = provCsv;
        if (deptCsv) payload.departmentIdsCsv = deptCsv;
        if (subpCsv) payload.subPrefectureIdsCsv = subpCsv;
        if (estabCsv) payload.establishmentIdsCsv = estabCsv;
      }
      return payload;
    }

    if (activeTab === 1) {
      const payload = {
        ...base,
        audienceType: "admin",
      };
      const provCsv = normalizeCsv(userProvinceIds);
      const deptCsv = normalizeCsv(userDepartmentIds);
      const subpCsv = normalizeCsv(userSubPrefectureIds);
      const estabCsv = normalizeCsv(userEstablishmentIds);
      if (provCsv) payload.provinceIdsCsv = provCsv;
      if (deptCsv) payload.departmentIdsCsv = deptCsv;
      if (subpCsv) payload.subPrefectureIdsCsv = subpCsv;
      if (estabCsv) payload.establishmentIdsCsv = estabCsv;
      return payload;
    }

    return {
      ...base,
      audienceType: "recruiter",
    };
  }, [
    activeTab,
    title,
    body,
    deviceTypesCsv,
    teacherScope,
    provinceIds,
    departmentIds,
    subPrefectureIds,
    establishmentIds,
    userProvinceIds,
    userDepartmentIds,
    userSubPrefectureIds,
    userEstablishmentIds,
  ]);

  const validateMessage = () => {
    if (!title.trim()) {
      setError(t("notifications.error.title_required", "Title is required."));
      return false;
    }
    if (!body.trim()) {
      setError(t("notifications.error.body_required", "Message is required."));
      return false;
    }
    return true;
  };

  const validateAudience = () => {
    if (!sendAndroid && !sendIos) {
      setError(t("notifications.error.device_required", "Select at least one device type (Android or iOS)."));
      return false;
    }

    if (activeTab === 0) {
      if (teacherScope === "filtered") {
        const provCsv = normalizeCsv(provinceIds);
        const deptCsv = normalizeCsv(departmentIds);
        const subpCsv = normalizeCsv(subPrefectureIds);
        const estabCsv = normalizeCsv(establishmentIds);
        if (!provCsv && !deptCsv && !subpCsv && !estabCsv) {
          setError(
            t(
              "notifications.error.filtered_required",
              "Select at least one location filter (province, department, sub-prefecture, or establishment)."
            )
          );
          return false;
        }
      }
    }
    if (activeTab === 1) {
      const provCsv = normalizeCsv(userProvinceIds);
      const deptCsv = normalizeCsv(userDepartmentIds);
      const subpCsv = normalizeCsv(userSubPrefectureIds);
      const estabCsv = normalizeCsv(userEstablishmentIds);
      if (!provCsv && !deptCsv && !subpCsv && !estabCsv) {
        setError(
          t(
            "notifications.error.admin_filters_required",
            "Select at least one location filter for panel users."
          )
        );
        return false;
      }
    }
    return true;
  };

  const handleSend = async () => {
    setError("");
    if (!validateMessage() || !validateAudience()) return;

    const audienceLabel =
      activeTab === 0
        ? scopeLabels[teacherScope]
        : activeTab === 1
          ? t("notifications.tab.users", "Panel users")
          : t("notifications.tab.recruiters", "Recruiters");

    const confirm = await Swal.fire({
      title: t("notifications.send_confirm_title", "Send push notification?"),
      html: t(
        "notifications.send_confirm_text",
        "This will send « {{title}} » to {{audience}}. Continue?"
      )
        .replace("{{title}}", title.trim())
        .replace("{{audience}}", audienceLabel),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("notifications.send", "Send"),
      cancelButtonText: t("notifications.cancel", "Cancel"),
      ...swalApp,
    });
    if (!confirm.isConfirmed) return;

    setSending(true);
    try {
      const { data } = await api.post(NOTIF_API.send, sendPayload, { headers: jsonHeaders() });
      const count =
        data?.successCount ??
        data?.recipientCount ??
        data?.sentCount ??
        data?.count ??
        data?.totalTargets ??
        "—";
      await Swal.fire({
        title: t("notifications.send_success_title", "Notification sent"),
        text: t("notifications.send_success_text", "Delivered to {{count}} recipient(s).").replace(
          "{{count}}",
          String(count)
        ),
        icon: "success",
        ...swalApp,
      });
      setTitle("");
      setBody("");
      if (historyPage === 1) fetchHistory();
      else setHistoryPage(1);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        t("notifications.error.send_failed", "Failed to send notification.");
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const showTeacherFilters = teacherScope === "filtered";
  const selectedProvinceCount = normalizeCsv(provinceIds).split(",").filter(Boolean).length;
  const selectedDepartmentCount = normalizeCsv(departmentIds).split(",").filter(Boolean).length;
  const selectedSubPrefectureCount = normalizeCsv(subPrefectureIds).split(",").filter(Boolean).length;
  const selectedEstablishmentCount = normalizeCsv(establishmentIds).split(",").filter(Boolean).length;
  const selectedUserSubPrefectureCount = normalizeCsv(userSubPrefectureIds).split(",").filter(Boolean).length;
  const selectedUserEstablishmentCount = normalizeCsv(userEstablishmentIds).split(",").filter(Boolean).length;

  const refreshTeacherSubprefectures = useCallback(
    (provCsv, deptCsv) => {
      if (!normalizeCsv(provCsv)) {
        setSubpOpts([]);
        return;
      }
      fetchSubprefecturesForScope({
        provIdsRaw: provCsv,
        deptIdsRaw: deptCsv,
        requestKey: "teacher",
        setOptions: setSubpOpts,
      });
    },
    [fetchSubprefecturesForScope]
  );

  const refreshTeacherEstablishments = useCallback(
    (provCsv, deptCsv, subpCsv) => {
      if (!normalizeCsv(provCsv) && !normalizeCsv(subpCsv)) {
        setEstabOpts([]);
        return;
      }
      fetchEstablishmentsForScope({
        provIdsRaw: provCsv,
        deptIdsRaw: deptCsv,
        subpIdsRaw: subpCsv,
        requestKey: "teacher",
        setOptions: setEstabOpts,
      });
    },
    [fetchEstablishmentsForScope]
  );

  const refreshUserSubprefectures = useCallback(
    (provCsv, deptCsv) => {
      if (!normalizeCsv(provCsv)) {
        setUserSubpOpts([]);
        return;
      }
      fetchSubprefecturesForScope({
        provIdsRaw: provCsv,
        deptIdsRaw: deptCsv,
        requestKey: "user",
        setOptions: setUserSubpOpts,
      });
    },
    [fetchSubprefecturesForScope]
  );

  const refreshUserEstablishments = useCallback(
    (provCsv, deptCsv, subpCsv) => {
      if (!normalizeCsv(provCsv) && !normalizeCsv(subpCsv)) {
        setUserEstabOpts([]);
        return;
      }
      fetchEstablishmentsForScope({
        provIdsRaw: provCsv,
        deptIdsRaw: deptCsv,
        subpIdsRaw: subpCsv,
        requestKey: "user",
        setOptions: setUserEstabOpts,
      });
    },
    [fetchEstablishmentsForScope]
  );

  const deviceTypesField = (
    <Grid item xs={12}>
      <SoftTypography component="label" sx={fieldLabelSx}>
        {t("notifications.device_types", "Device types")}
      </SoftTypography>
      <FormGroup row sx={{ gap: 1 }}>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={sendAndroid} onChange={(e) => setSendAndroid(e.target.checked)} />
          }
          label={t("notifications.device.android", "Android")}
        />
        <FormControlLabel
          control={
            <Checkbox size="small" checked={sendIos} onChange={(e) => setSendIos(e.target.checked)} />
          }
          label={t("notifications.device.ios", "iOS")}
        />
      </FormGroup>
      <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, display: "block", mt: 0.5 }}>
        {t("notifications.device_hint", "Sent to selected platforms only (Android = 1, iOS = 2).")}
      </SoftTypography>
    </Grid>
  );

  const historyTotalPages = totalPagesFromCount(historyTotal, historyPageSize);

  const jobTypeLabels = useMemo(
    () => ({
      teacher: t("notifications.tab.teachers", "Teachers"),
      admin: t("notifications.tab.users", "Panel users"),
      recruiter: t("notifications.tab.recruiters", "Recruiters"),
    }),
    [t]
  );

  const formatJobType = (jobType) => {
    const key = String(jobType ?? "").toLowerCase();
    return jobTypeLabels[key] ?? jobType ?? "—";
  };

  const pageSummary = useMemo(() => {
    const completed = history.filter((r) =>
      String(r?.status ?? "").toLowerCase().includes("complete")
    ).length;
    const failed = history.filter((r) => String(r?.status ?? "").toLowerCase().includes("fail")).length;
    const delivered = history.reduce((sum, r) => sum + (Number(r?.successCount) || 0), 0);
    return { completed, failed, delivered };
  }, [history]);

  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  const renderHistoryDetail = (row) => {
    const requestedOn = row?.requestedOn ?? row?.RequestedOn;
    const startedOn = row?.startedOn ?? row?.StartedOn;
    const completedOn = row?.completedOn ?? row?.CompletedOn;
    const failureReason = row?.failureReason ?? row?.FailureReason;
    const productIdsCsv = row?.productIdsCsv ?? row?.ProductIdsCsv;
    const hangfireJobId = row?.hangfireJobId ?? row?.HangfireJobId;

    const detailItems = [
      { label: t("notifications.history.requested", "Requested"), value: formatDateTime(requestedOn) },
      { label: t("notifications.history.started", "Started"), value: formatDateTime(startedOn) },
      { label: t("notifications.history.completed", "Completed"), value: formatDateTime(completedOn) },
      { label: t("notifications.history.job_id", "Job ID"), value: hangfireJobId ?? row?.id ?? "—" },
    ];
    if (productIdsCsv) {
      detailItems.push({
        label: t("notifications.history.filters", "Filters"),
        value: productIdsCsv,
      });
    }

    return (
      <Box sx={{ px: 2, py: 1.5, bgcolor: "rgba(247, 249, 252, 0.85)" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <SoftTypography variant="caption" sx={{ ...fieldLabelSx, mb: 0.5 }}>
              {t("notifications.field.body", "Message")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textPrimary, whiteSpace: "pre-wrap" }}>
              {row?.message ?? row?.Message ?? "—"}
            </SoftTypography>
            {failureReason && (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                {failureReason}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={1}>
              {detailItems.map(({ label, value }) => (
                <Box key={label} sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                  <SoftTypography variant="caption" sx={{ ...fieldLabelSx, mb: 0, minWidth: 88 }}>
                    {label}
                  </SoftTypography>
                  <SoftTypography variant="caption" sx={{ color: figmaTokens.textPrimary, fontWeight: 600 }}>
                    {value}
                  </SoftTypography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} px={{ xs: 2, md: 3 }}>
          <SoftTypography
            variant="caption"
            fontWeight="bold"
            sx={{ color: figmaTokens.accent, letterSpacing: "0.12em", display: "block", mb: 0.5 }}
          >
            {t("notifications.page_kicker", "COMMUNICATIONS")}
          </SoftTypography>
          <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mb: 0.5 }}>
            {t("notifications.title", "Push notifications")}
          </SoftTypography>
          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, maxWidth: 720 }}>
            {t(
              "notifications.page_hint",
              "Send mobile push notifications to teachers, panel users, or recruiters."
            )}
          </SoftTypography>
        </SoftBox>

        <SoftBox px={{ xs: 2, md: 3 }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} xl={5} lg={5}>
              <Card variant="outlined" sx={{ ...cardSx, mb: { xs: 0, lg: 0 } }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="fullWidth"
                  sx={{
                    px: 0.5,
                    borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                    minHeight: 48,
                    "& .MuiTab-root": {
                      fontWeight: 600,
                      textTransform: "none",
                      minHeight: 48,
                      py: 1,
                      fontSize: { xs: "0.72rem", sm: "0.82rem" },
                      minWidth: 0,
                      px: { xs: 0.5, sm: 1 },
                    },
                    "& .MuiTab-iconWrapper": { mr: { xs: 0.25, sm: 0.75 } },
                  }}
                >
                  <Tab label={t("notifications.tab.teachers", "Teachers")} icon={<Icon fontSize="small">school</Icon>} iconPosition="start" />
                  <Tab label={t("notifications.tab.users", "Panel users")} icon={<Icon fontSize="small">admin_panel_settings</Icon>} iconPosition="start" />
                  <Tab label={t("notifications.tab.recruiters", "Recruiters")} icon={<Icon fontSize="small">groups</Icon>} iconPosition="start" />
                </Tabs>

                <SoftBox p={{ xs: 2, md: 2.5 }} sx={{ flex: 1 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SoftTypography component="label" sx={fieldLabelSx}>
                        {t("notifications.field.title", "Title")}
                      </SoftTypography>
                      <SoftInput
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t("notifications.field.title_placeholder", "Notification title")}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SoftTypography component="label" sx={fieldLabelSx}>
                        {t("notifications.field.body", "Message")}
                      </SoftTypography>
                      <SoftInput
                        multiline
                        rows={3}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={t("notifications.field.body_placeholder", "Write the notification message…")}
                      />
                    </Grid>

                    {activeTab === 0 && (
                      <>
                        <Grid item xs={12}>
                          <SoftTypography component="label" sx={fieldLabelSx}>
                            {t("notifications.audience", "Audience")}
                          </SoftTypography>
                          <RadioGroup
                            row
                            value={teacherScope}
                            onChange={(e) => {
                              setTeacherScope(e.target.value);
                              setProvinceIds("");
                              setDepartmentIds("");
                              setSubPrefectureIds("");
                              setEstablishmentIds("");
                              setDeptOpts([]);
                              setSubpOpts([]);
                              setEstabOpts([]);
                            }}
                            sx={{ flexWrap: "wrap", gap: 0.5 }}
                          >
                            {TEACHER_SCOPES.map((scope) => (
                              <FormControlLabel
                                key={scope}
                                value={scope}
                                control={<Radio size="small" />}
                                label={scopeLabels[scope]}
                              />
                            ))}
                          </RadioGroup>
                        </Grid>

                        {deviceTypesField}

                        {showTeacherFilters && (
                          <>
                            <Grid item xs={12}>
                              <Alert severity="info" icon={<Icon fontSize="small">info</Icon>} sx={{ py: 0.5 }}>
                                {t(
                                  "notifications.filtered_hint",
                                  "Select provinces first, then optionally narrow by department, sub-prefecture, or establishment."
                                )}
                              </Alert>
                            </Grid>
                            <Grid item xs={12}>
                              <SoftTypography component="label" sx={fieldLabelSx}>
                                {t("notifications.provinces", "Provinces")}
                              </SoftTypography>
                              <Select2Field
                                name="provinceIds"
                                multiple
                                options={provOpts}
                                placeholder={t("notifications.provinces_placeholder", "Select one or more provinces")}
                                value={provinceIds}
                                onChange={(val) => {
                                  const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                                  setProvinceIds(csv);
                                  setDepartmentIds("");
                                  setSubPrefectureIds("");
                                  setEstablishmentIds("");
                                  if (csv) {
                                    fetchDepartmentsForProvinces(csv);
                                    refreshTeacherSubprefectures(csv, "");
                                    refreshTeacherEstablishments(csv, "", "");
                                  } else {
                                    setDeptOpts([]);
                                    setSubpOpts([]);
                                  }
                                  setEstabOpts([]);
                                }}
                              />
                              {selectedProvinceCount > 0 && (
                                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                                  {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedProvinceCount))}
                                </SoftTypography>
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <SoftTypography component="label" sx={fieldLabelSx}>
                                {t("notifications.departments", "Departments")}
                              </SoftTypography>
                              <Select2Field
                                name="departmentIds"
                                multiple
                                options={deptOpts}
                                placeholder={t("notifications.departments_placeholder", "Select one or more departments")}
                                value={departmentIds}
                                disabled={!normalizeCsv(provinceIds)}
                                onChange={(val) => {
                                  const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                                  setDepartmentIds(csv);
                                  setSubPrefectureIds("");
                                  setEstablishmentIds("");
                                  refreshTeacherSubprefectures(provinceIds, csv);
                                  refreshTeacherEstablishments(provinceIds, csv, "");
                                }}
                              />
                              {selectedDepartmentCount > 0 && (
                                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                                  {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedDepartmentCount))}
                                </SoftTypography>
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <SoftTypography component="label" sx={fieldLabelSx}>
                                {t("notifications.subprefectures", "Sub-prefectures")}
                              </SoftTypography>
                              <Select2Field
                                name="subPrefectureIds"
                                multiple
                                options={subpOpts}
                                placeholder={t(
                                  "notifications.subprefectures_placeholder",
                                  "Select one or more sub-prefectures"
                                )}
                                value={subPrefectureIds}
                                disabled={!normalizeCsv(provinceIds)}
                                onChange={(val) => {
                                  const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                                  setSubPrefectureIds(csv);
                                  setEstablishmentIds("");
                                  refreshTeacherEstablishments(provinceIds, departmentIds, csv);
                                }}
                              />
                              {selectedSubPrefectureCount > 0 && (
                                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                                  {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedSubPrefectureCount))}
                                </SoftTypography>
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <SoftTypography component="label" sx={fieldLabelSx}>
                                {t("notifications.establishments", "Establishments")}
                              </SoftTypography>
                              <Select2Field
                                name="establishmentIds"
                                multiple
                                options={estabOpts}
                                placeholder={t(
                                  "notifications.establishments_placeholder",
                                  "Select one or more establishments"
                                )}
                                value={establishmentIds}
                                disabled={!normalizeCsv(provinceIds)}
                                onChange={(val) => {
                                  const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                                  setEstablishmentIds(csv);
                                }}
                              />
                              {selectedEstablishmentCount > 0 && (
                                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                                  {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedEstablishmentCount))}
                                </SoftTypography>
                              )}
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                    {activeTab === 1 && (
                      <>
                        {deviceTypesField}
                        <Grid item xs={12}>
                          <Alert severity="info" icon={<Icon fontSize="small">info</Icon>} sx={{ py: 0.5 }}>
                            {t(
                              "notifications.users_hint",
                              "Send to panel users (admins) filtered by province, department, sub-prefecture, and/or establishment."
                            )}
                          </Alert>
                        </Grid>
                        <Grid item xs={12}>
                          <SoftTypography component="label" sx={fieldLabelSx}>
                            {t("notifications.provinces", "Provinces")}
                          </SoftTypography>
                          <Select2Field
                            name="userProvinceIds"
                            multiple
                            options={provOpts}
                            placeholder={t("notifications.provinces_placeholder", "Select one or more provinces")}
                            value={userProvinceIds}
                            onChange={(val) => {
                              const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                              setUserProvinceIds(csv);
                              setUserDepartmentIds("");
                              setUserSubPrefectureIds("");
                              setUserEstablishmentIds("");
                              if (csv) {
                                fetchDepartmentsForUserProvinces(csv);
                                refreshUserSubprefectures(csv, "");
                                refreshUserEstablishments(csv, "", "");
                              } else {
                                setUserDeptOpts([]);
                                setUserSubpOpts([]);
                              }
                              setUserEstabOpts([]);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SoftTypography component="label" sx={fieldLabelSx}>
                            {t("notifications.departments", "Departments")}
                          </SoftTypography>
                          <Select2Field
                            name="userDepartmentIds"
                            multiple
                            options={userDeptOpts}
                            placeholder={t("notifications.departments_placeholder", "Select one or more departments")}
                            value={userDepartmentIds}
                            disabled={!normalizeCsv(userProvinceIds)}
                            onChange={(val) => {
                              const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                              setUserDepartmentIds(csv);
                              setUserSubPrefectureIds("");
                              setUserEstablishmentIds("");
                              refreshUserSubprefectures(userProvinceIds, csv);
                              refreshUserEstablishments(userProvinceIds, csv, "");
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SoftTypography component="label" sx={fieldLabelSx}>
                            {t("notifications.subprefectures", "Sub-prefectures")}
                          </SoftTypography>
                          <Select2Field
                            name="userSubPrefectureIds"
                            multiple
                            options={userSubpOpts}
                            placeholder={t(
                              "notifications.subprefectures_placeholder",
                              "Select one or more sub-prefectures"
                            )}
                            value={userSubPrefectureIds}
                            disabled={!normalizeCsv(userProvinceIds)}
                            onChange={(val) => {
                              const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                              setUserSubPrefectureIds(csv);
                              setUserEstablishmentIds("");
                              refreshUserEstablishments(userProvinceIds, userDepartmentIds, csv);
                            }}
                          />
                          {selectedUserSubPrefectureCount > 0 && (
                            <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                              {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedUserSubPrefectureCount))}
                            </SoftTypography>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <SoftTypography component="label" sx={fieldLabelSx}>
                            {t("notifications.establishments", "Establishments")}
                          </SoftTypography>
                          <Select2Field
                            name="userEstablishmentIds"
                            multiple
                            options={userEstabOpts}
                            placeholder={t(
                              "notifications.establishments_placeholder",
                              "Select one or more establishments"
                            )}
                            value={userEstablishmentIds}
                            disabled={!normalizeCsv(userProvinceIds)}
                            onChange={(val) => {
                              const csv = Array.isArray(val) ? val.join(",") : normalizeCsv(val);
                              setUserEstablishmentIds(csv);
                            }}
                          />
                          {selectedUserEstablishmentCount > 0 && (
                            <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                              {t("notifications.selected_count", "{{n}} selected").replace("{{n}}", String(selectedUserEstablishmentCount))}
                            </SoftTypography>
                          )}
                        </Grid>
                      </>
                    )}

                    {activeTab === 2 && (
                      <>
                        {deviceTypesField}
                        <Grid item xs={12}>
                          <Alert severity="info" icon={<Icon fontSize="small">info</Icon>} sx={{ py: 0.5 }}>
                            {t(
                              "notifications.recruiters_hint",
                              "This notification will be sent to all registered recruiters with a valid push token."
                            )}
                          </Alert>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <SoftButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={handleSend}
                        disabled={sending}
                        sx={{ py: 1.1 }}
                      >
                        {sending ? t("notifications.sending", "Sending…") : t("notifications.send", "Send notification")}
                      </SoftButton>
                    </Grid>
                  </Grid>
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} xl={7} lg={7}>
              <Card variant="outlined" sx={cardSx}>
                <SoftBox
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 1.75,
                    borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, lineHeight: 1.2 }}>
                      {t("notifications.history_title", "Sent notifications")}
                    </SoftTypography>
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                      {t("notifications.history.subtitle", "{{total}} job(s) total").replace("{{total}}", String(historyTotal))}
                    </SoftTypography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={t("notifications.history.summary_completed", "{{n}} completed").replace("{{n}}", String(pageSummary.completed))}
                      sx={{ bgcolor: "rgba(34, 197, 94, 0.1)", color: "#15803d", fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={t("notifications.history.summary_failed", "{{n}} failed").replace("{{n}}", String(pageSummary.failed))}
                      sx={{ bgcolor: "rgba(239, 68, 68, 0.1)", color: "#b91c1c", fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={t("notifications.history.summary_delivered", "{{n}} delivered").replace("{{n}}", String(pageSummary.delivered))}
                      sx={{ bgcolor: "rgba(59, 130, 246, 0.1)", color: "#1d4ed8", fontWeight: 600 }}
                    />
                  </Stack>
                </SoftBox>

                {historyLoading && <Loading />}

                <TableContainer component={Paper} elevation={0} sx={{ flex: 1, maxHeight: { xs: "none", lg: "calc(100vh - 280px)" } }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      minWidth: 720,
                      ...directoryTableHeadStickyOverridesSx,
                      ...directoryTableBodyCellDefaultsSx,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.history.date", "Date")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.history.audience", "Audience")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.history.notification", "Notification")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.device_types", "Device types")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.history.delivery", "Delivery")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx}>{t("notifications.history.status", "Status")}</TableCell>
                        <TableCell sx={directoryTableHeaderCellSx} align="center" width={48} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!historyLoading && history.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                            <SoftTypography variant="body2" color="text">
                              {t("notifications.history.empty", "No notifications sent yet.")}
                            </SoftTypography>
                          </TableCell>
                        </TableRow>
                      )}
                      {history.map((row) => {
                        const id = row?.id ?? row?.hangfireJobId ?? row?.notificationId;
                        const jobType = row?.jobType ?? row?.JobType ?? row?.audienceType ?? row?.AudienceType;
                        const rowTitle = row?.title ?? row?.Title ?? "—";
                        const rowMessage = row?.message ?? row?.Message ?? "";
                        const status = row?.status ?? row?.Status ?? "—";
                        const statusStyle = statusTone(status);
                        const jobStyle = jobTypeTone(jobType);
                        const deviceTypes = parseDeviceTypes(row?.deviceTypesCsv ?? row?.DeviceTypesCsv);
                        const totalTargets = row?.totalTargets ?? row?.TotalTargets;
                        const successCount = row?.successCount ?? row?.SuccessCount;
                        const failureCount = row?.failureCount ?? row?.FailureCount;
                        const failureReason = row?.failureReason ?? row?.FailureReason;
                        const requestedOn = row?.requestedOn ?? row?.RequestedOn ?? row?.sentAt ?? row?.createdAt;
                        const isExpanded = expandedHistoryId === id;

                        return (
                          <React.Fragment key={id}>
                            <TableRow
                              hover
                              sx={{
                                "&:nth-of-type(4n+1) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                                cursor: failureReason || rowMessage ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (!failureReason && !rowMessage) return;
                                setExpandedHistoryId(isExpanded ? null : id);
                              }}
                            >
                              <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600, fontSize: "0.78rem" }}>
                                {formatDateTime(requestedOn)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={formatJobType(jobType)}
                                  size="small"
                                  sx={{
                                    bgcolor: jobStyle.bg,
                                    color: jobStyle.color,
                                    fontWeight: 700,
                                    fontSize: "0.68rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                <SoftTypography variant="caption" sx={{ fontWeight: 700, display: "block", color: figmaTokens.textPrimary }}>
                                  {rowTitle}
                                </SoftTypography>
                                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, display: "block" }}>
                                  {truncateText(rowMessage, 56)}
                                </SoftTypography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {deviceTypes.length === 0 && "—"}
                                  {deviceTypes.map((typeId) => (
                                    <Chip
                                      key={typeId}
                                      label={
                                        typeId === "1"
                                          ? t("notifications.device.android", "Android")
                                          : typeId === "2"
                                            ? t("notifications.device.ios", "iOS")
                                            : typeId
                                      }
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600 }}
                                    />
                                  ))}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                                <SoftTypography component="span" variant="caption" sx={{ color: "#15803d", fontWeight: 700 }}>
                                  {successCount ?? 0}
                                </SoftTypography>
                                <SoftTypography component="span" variant="caption" sx={{ color: figmaTokens.textMuted }}>
                                  {" / "}
                                </SoftTypography>
                                <SoftTypography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                                  {totalTargets ?? "—"}
                                </SoftTypography>
                                {Number(failureCount) > 0 && (
                                  <SoftTypography component="span" variant="caption" sx={{ color: "#b91c1c", ml: 0.75, fontWeight: 700 }}>
                                    ({failureCount} {t("notifications.history.failed_short", "failed")})
                                  </SoftTypography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={failureReason || status}>
                                  <Chip
                                    label={status}
                                    size="small"
                                    sx={{
                                      bgcolor: statusStyle.bg,
                                      color: statusStyle.color,
                                      border: `1px solid ${statusStyle.border}`,
                                      fontWeight: 700,
                                      fontSize: "0.68rem",
                                      maxWidth: 120,
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                <IconButton
                                  size="small"
                                  aria-label={t("notifications.history.details", "Details")}
                                  onClick={() => setExpandedHistoryId(isExpanded ? null : id)}
                                >
                                  <Icon fontSize="small">{isExpanded ? "expand_less" : "expand_more"}</Icon>
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  {renderHistoryDetail(row)}
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePaginationBar
                  page={historyPage}
                  totalCount={historyTotal}
                  pageSize={historyPageSize}
                  totalPages={historyTotalPages}
                  onPageChange={setHistoryPage}
                  onPageSizeChange={(size) => {
                    setHistoryPageSize(size);
                    setHistoryPage(1);
                  }}
                  pageSizeLabel={t("pagination.page_size", "Rows per page")}
                />
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
